/**
 * Step 0 — "Instructor" — [BUT-843, ours].
 *
 * Source selection is the heart of this step: a member holding an
 * active pack is offered ONLY `pack.allowedInstructors` — an empty whitelist
 * shows NOBODY, never the club list — and the source is gated on whether the
 * packs query has ANSWERED, never on `isLoading`. A FAILED packs query must
 * never fall back to the club instructor list, in the render AND in the
 * `useClubInstructors` `enabled` condition below.
 *
 * @module pages/lessons/steps/LessonInstructorStep
 */

import { CoachIcon } from '@butter/ui/icons';
import { RetryBanner, EmptyState } from '@butter/ui/components/feedback';
import { SnapCarousel } from '@butter/ui/components/data-display';
import { useAuth } from '~/services/auth';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useClubInstructors, useUserLessons, useLessonFlow } from '~/services/internalApi/lessons';
import type { ClubInstructor } from '~/services/internalApi/lessons';
import { resolveActivePack, lessonInstructorName } from '@butter/library/lessons';
import { InstructorCard } from './components';
import { TicketCtaButton } from '~/pages/booking/shared/TicketCtaButton';

export function LessonInstructorStep() {
  const { data: club } = useClubQuery();
  const { isAuthenticated } = useAuth();

  const {
    userLessons,
    hasAnswer: userLessonsHasAnswer,
    isLoading: userLessonsLoading,
    failure: userLessonsFailure,
    refetch: refetchUserLessons,
  } = useUserLessons({ enabled: isAuthenticated });

  // A guest cannot be asked for packs at all (`GET /lessons/user/:userId` is
  // `scopeToAuthenticatedUser`) — forcing `packsAnswered` true here means the
  // club list is never gated on a query that will never run.
  const packsAnswered = isAuthenticated ? userLessonsHasAnswer : true;
  const packsLoading = isAuthenticated && userLessonsLoading;
  const packsFailure = isAuthenticated ? userLessonsFailure : null;
  const activePack = isAuthenticated ? resolveActivePack(userLessons) : null;

  // Only ask the club endpoint once we KNOW there is no pack — never while the
  // packs answer is still in flight or has failed.
  const clubQuery = useClubInstructors({
    clubId: club?.id,
    enabled: packsAnswered && activePack === null,
  });

  const { instructorId, selectInstructor, goToStep } = useLessonFlow();

  const source: 'pack' | 'club' = activePack !== null ? 'pack' : 'club';
  const instructors: ClubInstructor[] =
    source === 'pack' ? (activePack?.allowedInstructors ?? []) : clubQuery.instructors;

  const isLoading = !packsAnswered || packsLoading || (activePack === null && clubQuery.isLoading);
  const isFailed = !!(packsFailure || clubQuery.failure || clubQuery.isError);
  const isEmptyPack = !isLoading && !isFailed && source === 'pack' && instructors.length === 0;
  const isEmptyClub = !isLoading && !isFailed && source === 'club' && instructors.length === 0;
  const hasInstructors = !isLoading && !isFailed && instructors.length > 0;

  const failureMessage = packsFailure?.message ?? clubQuery.failure?.message ?? "We couldn't load instructors.";

  const handleSelect = (instructor: ClubInstructor) => {
    selectInstructor({
      id: instructor.id,
      name: lessonInstructorName(instructor),
      image: instructor.image,
    });
    // No desktop Continue — selecting a card advances desktop straight to
    // Lesson Time. Phone keeps the sticky Continue button below.
    if (window.matchMedia('(min-width: 768px)').matches) {
      goToStep(1);
    }
  };

  const showContinue = hasInstructors && instructorId !== null;

  return (
    <div data-testid="lesson-instructor-step" className={showContinue ? 'pb-24 md:pb-0' : undefined}>
      {isLoading && (
        <div>
          <h2 className="text-xl font-semibold text-fg-secondary md:text-2xl">Select an instructor</h2>
          <div
            className="mt-4 flex gap-4 overflow-x-auto pb-2"
            aria-busy="true"
            data-testid="lesson-instructors-loading"
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-56 w-56 shrink-0 animate-pulse rounded-3xl bg-butter-gray-100"
              />
            ))}
          </div>
        </div>
      )}

      {!isLoading && isFailed && (
        <div>
          <h2 className="text-xl font-semibold text-fg-secondary md:text-2xl">Select an instructor</h2>
          <div className="mt-4">
            <RetryBanner
              message={failureMessage}
              onRetry={() => {
                if (packsFailure) void refetchUserLessons();
                else void clubQuery.refetch();
              }}
              testId="lesson-instructors-failed"
            />
          </div>
        </div>
      )}

      {!isLoading && !isFailed && isEmptyPack && (
        <div>
          <h2 className="text-xl font-semibold text-fg-secondary md:text-2xl">Select an instructor</h2>
          <div className="mt-4 rounded-3xl bg-bg-secondary">
            <EmptyState
              variant="muted"
              icon={<CoachIcon className="size-7 text-fg-muted" />}
              title="No instructors available"
              description="No instructor can teach against your lesson package right now. Please contact the club."
              data-testid="lesson-instructors-empty-pack"
            />
          </div>
        </div>
      )}

      {!isLoading && !isFailed && isEmptyClub && (
        <div>
          <h2 className="text-xl font-semibold text-fg-secondary md:text-2xl">Select an instructor</h2>
          <div className="mt-4 rounded-3xl bg-bg-secondary">
            <EmptyState
              variant="muted"
              icon={<CoachIcon className="size-7 text-fg-muted" />}
              title="No instructors available"
              description="This club hasn't set up any bookable instructors yet."
              data-testid="lesson-instructors-empty-club"
            />
          </div>
        </div>
      )}

      {hasInstructors && (
        <SnapCarousel
          as="list"
          title="Select an instructor"
          titleClassName="text-xl font-semibold text-fg-secondary md:text-2xl"
          headerClassName="flex items-center justify-between gap-2"
          navigationClassName="hidden gap-2 md:flex"
          className=""
          prevLabel="Previous instructors"
          nextLabel="Next instructors"
          prevTestId="lesson-instructor-carousel-prev"
          nextTestId="lesson-instructor-carousel-next"
          aria-label="Instructors"
          trackClassName="mt-5 flex flex-col gap-3 md:-mx-2 md:mt-6 md:flex-row md:snap-x md:snap-mandatory md:gap-0 md:overflow-x-auto md:scroll-smooth md:pb-2"
          itemClassName="w-full md:w-1/4 md:shrink-0 md:px-2 md:snap-start"
          items={instructors}
          getKey={(instructor) => instructor.id}
          renderItem={(instructor) => (
            <InstructorCard
              instructor={instructor}
              selected={instructor.id === instructorId}
              included={source === 'pack'}
              onSelect={handleSelect}
            />
          )}
        />
      )}

      {showContinue && (
        <TicketCtaButton
          variant="floating-mobile"
          label="Continue"
          ariaLabel="Continue to Lesson Time"
          onClick={() => goToStep(1)}
          disabled={false}
          busy={false}
          busyLabel="Continuing…"
          dataTestId="lesson-instructor-continue-mobile"
        />
      )}
    </div>
  );
}
